import {
  Bot,
  CircleStop,
  Clock,
  Filter,
  GitBranch,
  Keyboard,
  ListOrdered,
  type LucideIcon,
  MessageCircle,
  Timer,
  UserCheck,
  Webhook,
} from 'lucide-react';
import { Fragment } from 'react';
import { Handle, type NodeProps, Position } from 'reactflow';

export type NodeKind =
  | 'trigger'
  | 'message'
  | 'input'
  | 'condition'
  | 'condition_vars'
  | 'menu'
  | 'schedule_table'
  | 'transfer_human'
  | 'wait'
  | 'webhook'
  | 'end';

/** Opção do nó "Menu de opções" — uma mini-condição (operador + lista de valores). */
export interface MenuOption {
  /** Operador de match contra a resposta do contato (espelha o nó Condição). */
  op: 'contains' | 'equals' | 'not_equals' | 'empty' | 'not_empty';
  /** Valores/padrões — casa se QUALQUER um bater. Ignorado em empty/not_empty. */
  values: string[];
}

/** Rótulo curto do operador, pra exibir no nó. */
export const MENU_OP_TOKEN: Record<MenuOption['op'], string> = {
  contains: 'contém',
  equals: '=',
  not_equals: '≠',
  empty: 'vazio',
  not_empty: 'não vazio',
};

/** Comparador do nó "Condição com variáveis" (switch/case multi-ramo). */
export type VarComparator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'not_null';

/** Um ramo do nó "Condição com variáveis": comparador + lista de valores (chips). */
export interface VarBranch {
  comparator: VarComparator;
  /** Valores — casa se QUALQUER um bater. Ignorado em not_null. */
  values: string[];
}

/** Label PT-BR (dropdown) de cada comparador. */
export const VAR_COMPARATOR_LABEL: Record<VarComparator, string> = {
  eq: 'Igual a',
  neq: 'Diferente de',
  gt: 'Maior que',
  gte: 'Maior ou igual a',
  lt: 'Menor que',
  lte: 'Menor ou igual a',
  contains: 'Contém',
  not_null: 'Não é nulo',
};

/** Token curto do comparador, pra exibir no corpo do nó. */
export const VAR_COMPARATOR_TOKEN: Record<VarComparator, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  contains: 'contém',
  not_null: 'não nulo',
};

/** Faixa de horário do nó "Tabela de horários" (só Regular/semanal). */
export interface ScheduleWindow {
  /** 0=Dom … 6=Sáb */
  dayStart: number;
  dayEnd: number;
  startHour: number;
  startMin: number;
  endHour: number;
  endMin: number;
}

const WEEKDAY_ABBR = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Resumo curto de uma faixa, ex.: "Seg–Sex 07:00–22:30". */
export function summarizeScheduleWindow(w: ScheduleWindow): string {
  const days =
    w.dayStart === w.dayEnd
      ? WEEKDAY_ABBR[w.dayStart]
      : `${WEEKDAY_ABBR[w.dayStart]}–${WEEKDAY_ABBR[w.dayEnd]}`;
  return `${days} ${pad2(w.startHour)}:${pad2(w.startMin)}–${pad2(w.endHour)}:${pad2(w.endMin)}`;
}

export interface NodeStyle {
  icon: LucideIcon;
  label: string;
  description: string;
  color: string;
  bgClass: string;
  borderClass: string;
}

export const NODE_STYLES: Record<NodeKind, NodeStyle> = {
  trigger: {
    icon: Bot,
    label: 'Início',
    description: 'Trigger do fluxo',
    color: '#334155',
    // bg-n-500 (igual ao nó "Fim"): meio da escala = estável no dark. bg-n-700
    // invertia pra claro no modo escuro (header do Início ficava branco).
    bgClass: 'bg-n-500',
    borderClass: 'border-n-500',
  },
  message: {
    icon: MessageCircle,
    label: 'Mensagem',
    description: 'Texto, mídia ou template',
    color: '#0A4EE3',
    bgClass: 'bg-brand-500',
    borderClass: 'border-brand-500',
  },
  input: {
    icon: Keyboard,
    label: 'Entrada',
    description: 'Captura resposta do contato',
    color: '#EC4899',
    bgClass: 'bg-pink-500',
    borderClass: 'border-pink-500',
  },
  condition: {
    icon: Filter,
    label: 'Condição',
    description: 'SE/SENÃO baseado em variáveis',
    color: '#F59E0B',
    bgClass: 'bg-amber-500',
    borderClass: 'border-amber-500',
  },
  condition_vars: {
    icon: GitBranch,
    label: 'Condição com variáveis',
    description: 'Switch/case multi-ramo (comparador + valores)',
    color: '#0EA5A0',
    bgClass: 'bg-cyan-600',
    borderClass: 'border-cyan-600',
  },
  menu: {
    icon: ListOrdered,
    label: 'Menu de opções',
    description: 'Pergunta + opções (1, 2, 3…), uma saída por escolha',
    color: '#6366F1',
    bgClass: 'bg-indigo-500',
    borderClass: 'border-indigo-500',
  },
  schedule_table: {
    icon: Clock,
    label: 'Tabela de horários',
    description: 'Roteia por dia/hora (fuso do tenant)',
    color: '#14B8A6',
    bgClass: 'bg-teal-500',
    borderClass: 'border-teal-500',
  },
  transfer_human: {
    icon: UserCheck,
    label: 'Atendimento humano',
    description: 'Transfere pra fila/atendente',
    color: '#10B981',
    bgClass: 'bg-emerald-500',
    borderClass: 'border-emerald-500',
  },
  wait: {
    icon: Timer,
    label: 'Espera',
    description: 'Pausa N minutos antes de seguir',
    color: '#8B5CF6',
    bgClass: 'bg-violet-500',
    borderClass: 'border-violet-500',
  },
  webhook: {
    icon: Webhook,
    label: 'Webhook',
    description: 'Chama API externa',
    color: '#0EA5E9',
    bgClass: 'bg-sky-500',
    borderClass: 'border-sky-500',
  },
  end: {
    icon: CircleStop,
    label: 'Fim',
    description: 'Encerra o fluxo',
    color: '#64748B',
    bgClass: 'bg-n-500',
    borderClass: 'border-n-500',
  },
};

interface NodeData {
  kind: NodeKind;
  label?: string;
  config?: Record<string, unknown>;
}

function FlowNodeView({ data, selected }: NodeProps<NodeData>) {
  const style = NODE_STYLES[data.kind];
  const Icon = style.icon;
  const cfg = (data.config ?? {}) as Record<string, unknown>;

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-md border bg-n-0 shadow-md ${
        selected ? 'ring-2 ring-brand-500' : ''
      } ${style.borderClass}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 font-bold text-[12px] text-white ${style.bgClass}`}
      >
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-sm bg-white/20">
          <Icon size={13} strokeWidth={1.75} />
        </span>
        {data.label ?? style.label}
      </div>
      <div className="px-3 py-2.5 text-[11px] text-n-600">{summarize(data.kind, cfg)}</div>

      {/* Handles */}
      {data.kind !== 'trigger' && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: 'var(--bg-surface)',
            border: `2px solid ${style.color}`,
            width: 14,
            height: 14,
          }}
        />
      )}
      {data.kind === 'condition' ? (
        <>
          <Handle
            id="true"
            type="source"
            position={Position.Bottom}
            style={{
              background: 'var(--bg-surface)',
              border: '2px solid #10B981',
              left: '30%',
              width: 14,
              height: 14,
            }}
          />
          <span className="-translate-x-1/2 pointer-events-none absolute bottom-[-22px] left-[30%] text-[10px] font-bold text-emerald-600">
            SIM
          </span>
          <Handle
            id="false"
            type="source"
            position={Position.Bottom}
            style={{
              background: 'var(--bg-surface)',
              border: '2px solid #EF4444',
              left: '70%',
              width: 14,
              height: 14,
            }}
          />
          <span className="-translate-x-1/2 pointer-events-none absolute bottom-[-22px] left-[70%] text-[10px] font-bold text-rose-600">
            NÃO
          </span>
        </>
      ) : data.kind !== 'end' ? (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: 'var(--bg-surface)',
            border: `2px solid ${style.color}`,
            width: 14,
            height: 14,
          }}
        />
      ) : null}
    </div>
  );
}

function summarize(kind: NodeKind, cfg: Record<string, unknown>): string {
  switch (kind) {
    case 'trigger': {
      const t = cfg.trigger_type as string | undefined;
      if (t === 'keyword') {
        const kw = (cfg.keywords as string[] | undefined) ?? [];
        return `Palavra-chave: ${kw.length > 0 ? kw.join(', ') : 'configurar'}`;
      }
      if (t === 'first_contact') return 'Primeiro contato do contato';
      if (t === 'manual') return 'Disparado manualmente (broadcast)';
      if (t === 'schedule') return 'Agendado';
      return 'Qualquer mensagem';
    }
    case 'message': {
      const text = typeof cfg.text === 'string' ? (cfg.text as string) : '';
      return text ? text.slice(0, 80) + (text.length > 80 ? '…' : '') : 'Configurar mensagem';
    }
    case 'input': {
      const variable = (cfg.variable as string | undefined) ?? '';
      const inputType = (cfg.input_type as string | undefined) ?? 'text';
      const timeout = (cfg.timeout_minutes as number | undefined) ?? 5;
      const typeLabel =
        inputType === 'text'
          ? 'Texto livre'
          : inputType === 'number'
            ? 'Número'
            : inputType === 'email'
              ? 'E-mail'
              : inputType === 'buttons'
                ? 'Botões'
                : 'Lista';
      return variable
        ? `${typeLabel} → ${variable} · ${timeout}min`
        : `${typeLabel} · configurar variável`;
    }
    case 'condition': {
      const expr = typeof cfg.expression === 'string' ? cfg.expression : '';
      return expr ? `Se: ${expr.slice(0, 60)}` : 'Configurar condição';
    }
    case 'condition_vars': {
      const n = ((cfg.branches as unknown[] | undefined) ?? []).length;
      return n > 0 ? `${n} ramo(s) + Padrão` : 'Configurar ramos da condição';
    }
    case 'menu': {
      const n = ((cfg.options as unknown[] | undefined) ?? []).length;
      return n > 0 ? `${n} opção(ões) + Padrão` : 'Configurar opções do menu';
    }
    case 'schedule_table': {
      const n = ((cfg.windows as unknown[] | undefined) ?? []).length;
      return n > 0 ? `${n} faixa(s) + Padrão` : 'Configurar faixas de horário';
    }
    case 'transfer_human': {
      const dept = cfg.department_name as string | undefined;
      return dept ? `→ ${dept}` : 'Para fila geral';
    }
    case 'wait': {
      const seconds = typeof cfg.seconds === 'number' ? cfg.seconds : null;
      const mode = cfg.mode as string | undefined;
      if (mode === 'inbound') return 'Aguardar próximo inbound';
      return seconds ? `${seconds}s` : 'Configurar duração';
    }
    case 'webhook': {
      const url = typeof cfg.url === 'string' ? cfg.url : '';
      return url ? url.slice(0, 60) : 'Configurar URL';
    }
    case 'end':
      return 'Encerra o fluxo';
  }
}

/**
 * Nó "Menu de opções" — render dedicado por causa das saídas dinâmicas:
 * uma saída (handle `option:N`) por opção + uma "Padrão" (`default`). O motor
 * (worker-chatbot) envia o prompt, espera a resposta do contato, casa contra os
 * `key`s das opções e roteia pela `sourceHandle`.
 */
function MenuNodeView({ data, selected }: NodeProps<NodeData>) {
  const style = NODE_STYLES.menu;
  const Icon = style.icon;
  const cfg = (data.config ?? {}) as Record<string, unknown>;
  const options = (cfg.options as MenuOption[] | undefined) ?? [];
  const outputs = options.length + 1; // opções + Padrão
  const handleLeft = (idx: number): string => `${((idx + 1) / (outputs + 1)) * 100}%`;

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-md border bg-n-0 shadow-md ${
        selected ? 'ring-2 ring-brand-500' : ''
      } ${style.borderClass}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 font-bold text-[12px] text-white ${style.bgClass}`}
      >
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-sm bg-white/20">
          <Icon size={13} strokeWidth={1.75} />
        </span>
        {data.label ?? style.label}
      </div>
      <div className="px-3 py-2.5 text-[11px] text-n-600">
        {options.length === 0 ? (
          <span className="text-n-400">Configurar opções do menu</span>
        ) : (
          <ol className="space-y-1">
            {options.map((o, i) => {
              const vals = (o.values ?? []).filter((v) => v?.trim());
              const noVal = o.op === 'empty' || o.op === 'not_empty';
              return (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="font-bold text-indigo-600">{i + 1}.</span>
                  <span className="truncate">
                    {noVal ? (
                      <span className="text-n-500">resposta {MENU_OP_TOKEN[o.op]}</span>
                    ) : (
                      <>
                        <span className="text-n-400">{MENU_OP_TOKEN[o.op] ?? '='}</span>{' '}
                        {vals.length ? vals.map((v) => `"${v}"`).join(', ') : '(configurar)'}
                      </>
                    )}
                  </span>
                </li>
              );
            })}
            <li className="flex items-start gap-1.5 text-n-500">
              <span className="font-bold">P.</span>
              <span>Padrão (nenhuma casou)</span>
            </li>
          </ol>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: 'var(--bg-surface)',
          border: `2px solid ${style.color}`,
          width: 14,
          height: 14,
        }}
      />

      {options.map((_, i) => (
        <Fragment key={i}>
          <Handle
            id={`option:${i}`}
            type="source"
            position={Position.Bottom}
            style={{
              left: handleLeft(i),
              background: 'var(--bg-surface)',
              border: '2px solid #6366F1',
              width: 13,
              height: 13,
            }}
          />
          <span
            className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] max-w-[60px] truncate font-bold text-[10px] text-indigo-600"
            style={{ left: handleLeft(i) }}
          >
            {options[i]?.values?.find((v) => v?.trim()) || i + 1}
          </span>
        </Fragment>
      ))}
      <Handle
        id="default"
        type="source"
        position={Position.Bottom}
        style={{
          left: handleLeft(options.length),
          background: 'var(--bg-surface)',
          border: '2px solid #94A3B8',
          width: 13,
          height: 13,
        }}
      />
      <span
        className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] font-bold text-[10px] text-n-500"
        style={{ left: handleLeft(options.length) }}
      >
        Pad
      </span>
    </div>
  );
}

/**
 * Nó "Condição com variáveis" — render dedicado por causa das saídas dinâmicas:
 * uma saída (handle `branch:N`) por ramo + uma "Padrão" (`default`). O motor
 * (worker-chatbot) renderiza o `content`, avalia os ramos EM ORDEM e roteia
 * pela `sourceHandle` do 1º que casar (ou `default`).
 */
function ConditionVarsNodeView({ data, selected }: NodeProps<NodeData>) {
  const style = NODE_STYLES.condition_vars;
  const Icon = style.icon;
  const cfg = (data.config ?? {}) as Record<string, unknown>;
  const branches = (cfg.branches as VarBranch[] | undefined) ?? [];
  const content = (cfg.content as string | undefined) ?? '';
  // O que está sendo comparado, em linguagem simples (igual ao seletor "Comparar").
  const sourceLabel = /\{\{\s*message\b/.test(content) ? 'mensagem do contato' : content || '—';
  const outputs = branches.length + 1; // ramos + Padrão
  const handleLeft = (idx: number): string => `${((idx + 1) / (outputs + 1)) * 100}%`;

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-md border bg-n-0 shadow-md ${
        selected ? 'ring-2 ring-brand-500' : ''
      } ${style.borderClass}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 font-bold text-[12px] text-white ${style.bgClass}`}
      >
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-sm bg-white/20">
          <Icon size={13} strokeWidth={1.75} />
        </span>
        {data.label ?? style.label}
      </div>
      <div className="px-3 py-2.5 text-[11px] text-n-600">
        <div className="mb-1.5 truncate text-[10px] text-n-500">
          Compara: <span className="text-n-700">{sourceLabel}</span>
        </div>
        {branches.length === 0 ? (
          <span className="text-n-400">Configurar ramos da condição</span>
        ) : (
          <ol className="space-y-1">
            {branches.map((b, i) => {
              const vals = (b.values ?? []).filter((v) => v?.trim());
              const noVal = b.comparator === 'not_null';
              const token = VAR_COMPARATOR_TOKEN[b.comparator] ?? '=';
              return (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="font-bold text-cyan-700">{i + 1}.</span>
                  <span className="truncate">
                    {noVal ? (
                      <span className="text-n-500">{token}</span>
                    ) : (
                      <>
                        <span className="text-n-400">{token}</span>{' '}
                        {vals.length ? vals.map((v) => `"${v}"`).join(', ') : '(configurar)'}
                      </>
                    )}
                  </span>
                </li>
              );
            })}
            <li className="flex items-start gap-1.5 text-n-500">
              <span className="font-bold">P.</span>
              <span>Padrão (nenhum casou)</span>
            </li>
          </ol>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: 'var(--bg-surface)',
          border: `2px solid ${style.color}`,
          width: 14,
          height: 14,
        }}
      />

      {branches.map((_, i) => (
        <Fragment key={i}>
          <Handle
            id={`branch:${i}`}
            type="source"
            position={Position.Bottom}
            style={{
              left: handleLeft(i),
              background: 'var(--bg-surface)',
              border: '2px solid #0EA5A0',
              width: 13,
              height: 13,
            }}
          />
          <span
            className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] max-w-[60px] truncate font-bold text-[10px] text-cyan-700"
            style={{ left: handleLeft(i) }}
          >
            {branches[i]?.values?.find((v) => v?.trim()) || i + 1}
          </span>
        </Fragment>
      ))}
      <Handle
        id="default"
        type="source"
        position={Position.Bottom}
        style={{
          left: handleLeft(branches.length),
          background: 'var(--bg-surface)',
          border: '2px solid #94A3B8',
          width: 13,
          height: 13,
        }}
      />
      <span
        className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] font-bold text-[10px] text-n-500"
        style={{ left: handleLeft(branches.length) }}
      >
        Pad
      </span>
    </div>
  );
}

/**
 * Nó "Tabela de horários" — render dedicado por causa das saídas dinâmicas:
 * uma saída (handle) por faixa + uma "Padrão". O corpo lista as faixas numeradas,
 * e os handles na base seguem a mesma numeração (1, 2, … + Pad). O motor roteia
 * pela `sourceHandle` (`window:N` ou `default`).
 */
function ScheduleTableNodeView({ data, selected }: NodeProps<NodeData>) {
  const style = NODE_STYLES.schedule_table;
  const Icon = style.icon;
  const cfg = (data.config ?? {}) as Record<string, unknown>;
  const windows = (cfg.windows as ScheduleWindow[] | undefined) ?? [];
  const outputs = windows.length + 1; // faixas + Padrão
  const handleLeft = (idx: number): string => `${((idx + 1) / (outputs + 1)) * 100}%`;

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-md border bg-n-0 shadow-md ${
        selected ? 'ring-2 ring-brand-500' : ''
      } ${style.borderClass}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 font-bold text-[12px] text-white ${style.bgClass}`}
      >
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-sm bg-white/20">
          <Icon size={13} strokeWidth={1.75} />
        </span>
        {data.label ?? style.label}
      </div>
      <div className="px-3 py-2.5 text-[11px] text-n-600">
        {windows.length === 0 ? (
          <span className="text-n-400">Configurar faixas de horário</span>
        ) : (
          <ol className="space-y-1">
            {windows.map((w, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="font-bold text-teal-600">{i + 1}.</span>
                <span className="truncate">{summarizeScheduleWindow(w)}</span>
              </li>
            ))}
            <li className="flex items-start gap-1.5 text-n-500">
              <span className="font-bold">P.</span>
              <span>Padrão (nenhuma faixa)</span>
            </li>
          </ol>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: 'var(--bg-surface)',
          border: `2px solid ${style.color}`,
          width: 14,
          height: 14,
        }}
      />

      {windows.map((_, i) => (
        <Fragment key={i}>
          <Handle
            id={`window:${i}`}
            type="source"
            position={Position.Bottom}
            style={{
              left: handleLeft(i),
              background: 'var(--bg-surface)',
              border: '2px solid #14B8A6',
              width: 13,
              height: 13,
            }}
          />
          <span
            className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] font-bold text-[10px] text-teal-600"
            style={{ left: handleLeft(i) }}
          >
            {i + 1}
          </span>
        </Fragment>
      ))}
      <Handle
        id="default"
        type="source"
        position={Position.Bottom}
        style={{
          left: handleLeft(windows.length),
          background: 'var(--bg-surface)',
          border: '2px solid #94A3B8',
          width: 13,
          height: 13,
        }}
      />
      <span
        className="-translate-x-1/2 pointer-events-none absolute bottom-[-20px] font-bold text-[10px] text-n-500"
        style={{ left: handleLeft(windows.length) }}
      >
        Pad
      </span>
    </div>
  );
}

export const nodeTypes = {
  trigger: FlowNodeView,
  message: FlowNodeView,
  input: FlowNodeView,
  condition: FlowNodeView,
  condition_vars: ConditionVarsNodeView,
  menu: MenuNodeView,
  schedule_table: ScheduleTableNodeView,
  transfer_human: FlowNodeView,
  wait: FlowNodeView,
  webhook: FlowNodeView,
  end: FlowNodeView,
};
